import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface PdfListaParams {
  sorteo: { nombre: string; tipo_id: string; estado?: string; fecha?: string };
  fecha: string;
  paqueteNombre: string | null;
  localNombre: string | null;
  tipo: string;
  rows: any[];
}

// ---------- formato y banner ----------

const fmt = (v: unknown): string => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n).toLocaleString('es-CR') : '–';
};

const fmtNum = (v: unknown): string => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n).toLocaleString('es-CR') : '0';
};

// Si el sorteo no está cerrado/liquidado, marcamos PRELIMINAR.
function estadoBanner(estadoRaw?: string): { texto: string; preliminar: boolean } {
  const e = (estadoRaw || '').toUpperCase().trim();
  if (e === 'CERRADO' || e === 'CERRADA' || e === 'LIQUIDADO') {
    return { texto: e, preliminar: false };
  }
  if (!e) {
    return { texto: 'PRELIMINAR · LISTA NO CERRADA', preliminar: true };
  }
  return { texto: `PRELIMINAR · SORTEO ${e}`, preliminar: true };
}

// Encabezado compacto. Devuelve Y donde inicia el contenido.
function dibujarEncabezado(doc: jsPDF, params: PdfListaParams): number {
  const { sorteo, fecha, paqueteNombre, localNombre } = params;
  const ahora = new Date().toLocaleString('es-CR');
  const banner = estadoBanner(sorteo.estado);
  const pageW = doc.internal.pageSize.getWidth();
  const M = 6;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(0, 0, 0);
  doc.text('Lista Consolidada', M, 9);

  doc.setFontSize(9);
  const bannerW = doc.getTextWidth(banner.texto) + 6;
  if (banner.preliminar) {
    doc.setFillColor(220, 38, 38);
  } else {
    doc.setFillColor(34, 197, 94);
  }
  doc.setTextColor(255, 255, 255);
  doc.rect(pageW - M - bannerW, 4, bannerW, 6, 'F');
  doc.text(banner.texto, pageW - M - bannerW + 3, 8.5);
  doc.setTextColor(0, 0, 0);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  const partes: string[] = [];
  partes.push(`Sorteo: ${sorteo.nombre} (${sorteo.tipo_id})`);
  partes.push(`Fecha sorteo: ${sorteo.fecha || fecha}`);
  if (fecha && sorteo.fecha && fecha !== sorteo.fecha) partes.push(`Operativa: ${fecha}`);
  if (paqueteNombre) partes.push(`Paquete: ${paqueteNombre}`);
  if (localNombre) partes.push(`Local: ${localNombre}`);
  doc.text(partes.join('  ·  '), M, 14);

  doc.setFontSize(7.5);
  doc.setTextColor(90, 90, 90);
  doc.text(`Generado: ${ahora}`, M, 18);
  doc.setTextColor(0, 0, 0);

  return 21;
}

// ---------- paleta y márgenes (rediseño v2) ----------

const COLOR_FILA_VENTA: [number, number, number] = [255, 247, 194]; // #FFF7C2
const COLOR_TEXTO_VACIO: [number, number, number] = [180, 180, 180];
const COLOR_HEADER_BG: [number, number, number] = [30, 64, 175];
const MARGEN_LR = 6; // mm

// Calcula anchos proporcionales según el ancho disponible para llenar la página.
// `colsMontoPorGrupo` = columnas de monto por grupo (NORMAL/FECHAS=1, REVENTADO=2).
function calcularAnchos(doc: jsPDF, colsMontoPorGrupo: number): {
  anchoNum: number;
  anchoMonto: number;
  anchoSep: number;
} {
  const pageW = doc.internal.pageSize.getWidth();
  const usable = pageW - 2 * MARGEN_LR;
  const anchoSep = 3;
  const restante = usable - 4 * anchoSep; // 4 separadores entre 5 grupos
  const anchoGrupo = restante / 5;
  // Dentro del grupo: 1 columna # estrecha + N columnas de monto.
  // # toma ~22%; el resto se reparte entre las columnas de monto.
  const anchoNum = Math.round(anchoGrupo * 0.22 * 100) / 100;
  const anchoMonto = Math.round(((anchoGrupo - anchoNum) / colsMontoPorGrupo) * 100) / 100;
  return { anchoNum, anchoMonto, anchoSep };
}

// ---------- cuerpo NORMAL/FECHAS: # | MONTO × 5 grupos ----------

function dibujarGridMontoUnico(
  doc: jsPDF,
  startY: number,
  rows: any[],
): { total: number; finalY: number } {
  const map = new Map<string, any>();
  for (const r of rows) map.set(String(r.numero).padStart(2, '0'), r);

  let total = 0;
  const montos: number[] = new Array(100).fill(0);
  for (let n = 0; n < 100; n++) {
    const k = String(n).padStart(2, '0');
    const r = map.get(k);
    if (!r) continue;
    const t = Number(r.monto_total ?? r.vendido) || 0;
    montos[n] = t;
    total += t;
  }

  const { anchoNum, anchoMonto, anchoSep } = calcularAnchos(doc, 1);

  // 14 columnas: por cada grupo b in 0..4 → [#, MONTO] y SEP entre grupos.
  // Layout final: G0(#,M) SEP G1(#,M) SEP G2(#,M) SEP G3(#,M) SEP G4(#,M)
  // Indice de columna = b*3 (#), b*3+1 (MONTO), b*3+2 (SEP, salvo el último grupo).
  const headers: string[] = [];
  for (let b = 0; b < 5; b++) {
    headers.push('#', 'MONTO');
    if (b < 4) headers.push('');
  }

  const body: string[][] = [];
  for (let r = 0; r < 20; r++) {
    const row: string[] = [];
    for (let b = 0; b < 5; b++) {
      const n = b * 20 + r;
      const k = String(n).padStart(2, '0');
      const monto = montos[n];
      row.push(k, monto > 0 ? fmt(monto) : '–');
      if (b < 4) row.push('');
    }
    body.push(row);
  }

  const columnStyles: Record<number, any> = {};
  for (let b = 0; b < 5; b++) {
    const colNum = b * 3;
    const colMonto = b * 3 + 1;
    columnStyles[colNum] = { halign: 'center', fontStyle: 'bold', cellWidth: anchoNum };
    columnStyles[colMonto] = { halign: 'right', cellWidth: anchoMonto };
    if (b < 4) {
      columnStyles[b * 3 + 2] = {
        cellWidth: anchoSep,
        fillColor: [255, 255, 255],
        lineWidth: 0,
      };
    }
  }

  autoTable(doc, {
    head: [headers],
    body,
    startY,
    margin: { left: MARGEN_LR, right: MARGEN_LR, bottom: 16 },
    styles: {
      fontSize: 9,
      cellPadding: 1.4,
      lineWidth: 0.15,
      lineColor: [180, 180, 180],
      overflow: 'linebreak',
      valign: 'middle',
    },
    headStyles: {
      fillColor: COLOR_HEADER_BG,
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'center',
      cellPadding: 1.4,
    },
    columnStyles,
    pageBreak: 'avoid',
    theme: 'grid',
    didParseCell(data) {
      const colIdx = data.column.index;
      // Columna separadora: blanco sin bordes en cualquier sección
      if (colIdx % 3 === 2) {
        data.cell.styles.fillColor = [255, 255, 255];
        data.cell.styles.lineWidth = 0;
        data.cell.text = [''];
        return;
      }
      if (data.section !== 'body') return;

      const grupo = Math.floor(colIdx / 3);
      const rowIdx = data.row.index;
      const numero = grupo * 20 + rowIdx;
      const monto = montos[numero] || 0;
      const esMonto = colIdx % 3 === 1;

      if (monto > 0) {
        data.cell.styles.fillColor = COLOR_FILA_VENTA;
        data.cell.styles.textColor = [0, 0, 0];
        if (esMonto) data.cell.styles.fontStyle = 'bold';
      } else if (esMonto) {
        data.cell.styles.textColor = COLOR_TEXTO_VACIO;
      }
    },
  });

  const finalY = (doc as any).lastAutoTable?.finalY ?? startY + 100;
  return { total, finalY };
}

// ---------- cuerpo REVENTADO: # | R1 | R2 × 5 grupos ----------

function dibujarGridReventado(
  doc: jsPDF,
  startY: number,
  rows: any[],
): { totalR1: number; totalR2: number; finalY: number } {
  const map = new Map<string, any>();
  for (const r of rows) map.set(String(r.numero).padStart(2, '0'), r);

  const r1Arr: number[] = new Array(100).fill(0);
  const r2Arr: number[] = new Array(100).fill(0);
  let totalR1 = 0;
  let totalR2 = 0;
  for (let n = 0; n < 100; n++) {
    const k = String(n).padStart(2, '0');
    const r = map.get(k);
    if (!r) continue;
    r1Arr[n] = Number(r.vendido_rev1) || 0;
    r2Arr[n] = Number(r.vendido_rev2) || 0;
    totalR1 += r1Arr[n];
    totalR2 += r2Arr[n];
  }

  const { anchoNum, anchoMonto, anchoSep } = calcularAnchos(doc, 2);

  // 5 grupos × 3 columnas + 4 separadores = 19 columnas
  // Indices por grupo b: b*4 (#), b*4+1 (R1), b*4+2 (R2), b*4+3 (SEP, salvo el último).
  const headers: string[] = [];
  for (let b = 0; b < 5; b++) {
    headers.push('#', 'R1', 'R2');
    if (b < 4) headers.push('');
  }

  const body: string[][] = [];
  for (let r = 0; r < 20; r++) {
    const row: string[] = [];
    for (let b = 0; b < 5; b++) {
      const n = b * 20 + r;
      const k = String(n).padStart(2, '0');
      const v1 = r1Arr[n];
      const v2 = r2Arr[n];
      row.push(k, v1 > 0 ? fmt(v1) : '–', v2 > 0 ? fmt(v2) : '–');
      if (b < 4) row.push('');
    }
    body.push(row);
  }

  const columnStyles: Record<number, any> = {};
  for (let b = 0; b < 5; b++) {
    const baseCol = b * 4;
    columnStyles[baseCol] = { halign: 'center', fontStyle: 'bold', cellWidth: anchoNum };
    columnStyles[baseCol + 1] = { halign: 'right', cellWidth: anchoMonto };
    columnStyles[baseCol + 2] = { halign: 'right', cellWidth: anchoMonto };
    if (b < 4) {
      columnStyles[baseCol + 3] = {
        cellWidth: anchoSep,
        fillColor: [255, 255, 255],
        lineWidth: 0,
      };
    }
  }

  autoTable(doc, {
    head: [headers],
    body,
    startY,
    margin: { left: MARGEN_LR, right: MARGEN_LR, bottom: 16 },
    styles: {
      fontSize: 8.5,
      cellPadding: 1.2,
      lineWidth: 0.15,
      lineColor: [180, 180, 180],
      overflow: 'linebreak',
      valign: 'middle',
    },
    headStyles: {
      fillColor: COLOR_HEADER_BG,
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 8.5,
      halign: 'center',
      cellPadding: 1.2,
    },
    columnStyles,
    pageBreak: 'avoid',
    theme: 'grid',
    didParseCell(data) {
      const colIdx = data.column.index;
      if (colIdx % 4 === 3) {
        data.cell.styles.fillColor = [255, 255, 255];
        data.cell.styles.lineWidth = 0;
        data.cell.text = [''];
        return;
      }
      if (data.section !== 'body') return;

      const grupo = Math.floor(colIdx / 4);
      const rowIdx = data.row.index;
      const numero = grupo * 20 + rowIdx;
      const v1 = r1Arr[numero] || 0;
      const v2 = r2Arr[numero] || 0;
      const total = v1 + v2;
      const subCol = colIdx % 4; // 0=#, 1=R1, 2=R2

      if (total > 0) {
        data.cell.styles.fillColor = COLOR_FILA_VENTA;
        data.cell.styles.textColor = [0, 0, 0];
        if (subCol === 1 && v1 > 0) data.cell.styles.fontStyle = 'bold';
        if (subCol === 2 && v2 > 0) data.cell.styles.fontStyle = 'bold';
        if (subCol === 1 && v1 === 0) data.cell.styles.textColor = COLOR_TEXTO_VACIO;
        if (subCol === 2 && v2 === 0) data.cell.styles.textColor = COLOR_TEXTO_VACIO;
      } else if (subCol === 1 || subCol === 2) {
        data.cell.styles.textColor = COLOR_TEXTO_VACIO;
      }
    },
  });

  const finalY = (doc as any).lastAutoTable?.finalY ?? startY + 100;
  return { totalR1, totalR2, finalY };
}

// ---------- footer único ----------

function dibujarFooterTotal(doc: jsPDF, y: number, texto: string) {
  const pageW = doc.internal.pageSize.getWidth();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  const textW = doc.getTextWidth(texto);
  doc.text(texto, (pageW - textW) / 2, y + 8);
}

// ---------- pie de página (paginación) ----------

function pieDePagina(doc: jsPDF) {
  const total = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text(
      `Página ${i} de ${total}`,
      doc.internal.pageSize.getWidth() - 25,
      doc.internal.pageSize.getHeight() - 4,
    );
    doc.setTextColor(0, 0, 0);
  }
}

// ---------- núcleo único: construye el doc completo ----------

function construirDoc(params: PdfListaParams): jsPDF {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
  const { tipo, rows } = params;
  const startY = dibujarEncabezado(doc, params);

  if (tipo === 'NORMAL' || tipo === 'FECHAS') {
    const { total, finalY } = dibujarGridMontoUnico(doc, startY, rows);
    dibujarFooterTotal(doc, finalY, `TOTAL MONTO: ${fmtNum(total)}`);
  } else if (tipo === 'REVENTADO') {
    const { totalR1, totalR2, finalY } = dibujarGridReventado(doc, startY, rows);
    dibujarFooterTotal(
      doc,
      finalY,
      `TOTAL R1: ${fmtNum(totalR1)}  ·  TOTAL R2: ${fmtNum(totalR2)}  ·  TOTAL: ${fmtNum(totalR1 + totalR2)}`,
    );
  } else if (tipo === 'MEGA') {
    const filtered = rows.filter(
      (r) => (Number(r.vendido_mega) || 0) + (Number(r.vendido_rev1) || 0) + (Number(r.vendido_rev2) || 0) > 0,
    );
    const head = [['#', 'NUM2', 'REV1', 'REV2', 'MEGA']];
    const body: (string | number)[][] = filtered.map((r) => [
      r.numero, r.num2 || '', fmt(r.vendido_rev1), fmt(r.vendido_rev2), fmt(r.vendido_mega),
    ]);
    const tR1 = filtered.reduce((s, r) => s + (Number(r.vendido_rev1) || 0), 0);
    const tR2 = filtered.reduce((s, r) => s + (Number(r.vendido_rev2) || 0), 0);
    const tM = filtered.reduce((s, r) => s + (Number(r.vendido_mega) || 0), 0);
    body.push(['TOTAL', '', fmt(tR1), fmt(tR2), fmt(tM)]);
    autoTable(doc, {
      head, body, startY,
      margin: { left: MARGEN_LR, right: MARGEN_LR },
      styles: { fontSize: 7.5, cellPadding: 1 },
      headStyles: { fillColor: COLOR_HEADER_BG, textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      didParseCell(data) {
        if (data.row.index === body.length - 1 && data.section === 'body') {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [220, 220, 220];
        }
      },
      theme: 'grid',
    });
  } else if (tipo === 'MONAZOS') {
    const filtered = rows.filter((r) => (Number(r.vendido) || 0) > 0);
    const head = [['#', 'TIPO', 'VENDIDO']];
    const body: (string | number)[][] = filtered.map((r) => [r.numero, r.tipo || '', fmt(r.vendido)]);
    const t = filtered.reduce((s, r) => s + (Number(r.vendido) || 0), 0);
    body.push(['', 'TOTAL', fmt(t)]);
    autoTable(doc, {
      head, body, startY,
      margin: { left: MARGEN_LR, right: MARGEN_LR },
      styles: { fontSize: 7.5, cellPadding: 1 },
      headStyles: { fillColor: COLOR_HEADER_BG, textColor: 255, fontStyle: 'bold' },
      theme: 'grid',
    });
  } else if (tipo === 'PARLEY') {
    const filtered = rows.filter((r) => (Number(r.vendido) || 0) > 0);
    const head = [['NUM1', 'NUM2', 'VENDIDO']];
    const body: (string | number)[][] = filtered.map((r) => [r.num1 || '', r.num2 || '', fmt(r.vendido)]);
    const t = filtered.reduce((s, r) => s + (Number(r.vendido) || 0), 0);
    body.push(['', 'TOTAL', fmt(t)]);
    autoTable(doc, {
      head, body, startY,
      margin: { left: MARGEN_LR, right: MARGEN_LR },
      styles: { fontSize: 7.5, cellPadding: 1 },
      headStyles: { fillColor: COLOR_HEADER_BG, textColor: 255, fontStyle: 'bold' },
      theme: 'grid',
    });
  } else {
    autoTable(doc, {
      head: [['Dato']],
      body: [['Tipo no soportado para PDF']],
      startY,
      margin: { left: MARGEN_LR, right: MARGEN_LR },
    });
  }

  pieDePagina(doc);
  return doc;
}

// ---------- API pública ----------

export function generarPdfLista(params: PdfListaParams) {
  const doc = construirDoc(params);
  const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const safeName = params.sorteo.nombre.replace(/[^a-zA-Z0-9_-]/g, '_');
  doc.save(`lista-${safeName}-${params.fecha}-${ts}.pdf`);
}

/**
 * Igual que generarPdfLista pero devuelve el PDF como Blob en lugar de
 * disparar la descarga del navegador. Útil para compartir via Web Share API.
 */
export async function generarPdfListaBlob(params: PdfListaParams): Promise<Blob> {
  const doc = construirDoc(params);
  return doc.output('blob');
}
